import { css, StyleSheet } from 'aphrodite';
import React from 'react';
import Tooltip from '@material-ui/core/Tooltip';
import { Link } from 'react-router-dom';

import { ApiHelper } from '../../../common/helpers/ApiHelper';
import { Helper } from '../../../common/helpers/Helper';
import { BaseEntityPageSlixta } from '../../../components/BaseEntityPageSlixta';
import { UiHelper } from '../../../common/helpers/UiHelper';
import { AppConfig } from '../../../AppConfig';
import { AppStyles } from '../../../styles/AppTheme';
import { Controller } from '../../../common/Controller';
import { CdpHelper } from '../../../helpers/CdpHelper';
import { AppHelper } from '../../../helpers/AppHelper';
import { CRMHelper } from '../../../helpers/CRMHelper';

function getCampaignFields(obj) {
  const transformField = (field, iType) => {
    const outField = JSON.parse(JSON.stringify(field))
    if (field.condition && ((field.condition.value && field.condition.value === iType) || (field.condition.values && field.condition.values.includes(iType)))) {
      delete outField.condition
    }
    return outField
  }
  return {
    AD: obj.fieldDefCampaignAd.steps[0].fields.map(f => transformField(f, 'AD')),
    BROADCAST: obj.fieldDefCampaignBroadcast.steps[0].fields.map(f => transformField(f, 'BROADCAST')),
    TRANSACTIONAL: obj.fieldDefCampaignTransactional.steps[0].fields.map(f => transformField(f, 'TRANSACTIONAL')),
    NOTIFICATION: obj.fieldDefCampaignNotification.steps[0].fields.map(f => transformField(f, 'NOTIFICATION')),
    DRIP: obj.fieldDefCampaignDrip.steps[0].fields.map(f => transformField(f, 'DRIP')),
  }[obj.campaignType] || []
}
function restrictChannels(fieldDef, channels, defaultValue) {
  const channelField = fieldDef.steps[0].fields.find(f => f.key === 'channel')
  channelField.options = channelField.options.filter(o => channels.includes(o.key))
  channelField.defaultValue = defaultValue
}

function findTotalDelayInDays(steps) {
  let totalDelay = 0;
  for (const key in steps) {
    if (steps[key].stepTimeDelayDays) {
      totalDelay += Number(steps[key].stepTimeDelayDays);
    }
  }
  return totalDelay
}

function mapSegmentOptions(segments) {
  return segments.map(s => ({ key: s.id, text: s.title }))
}

function setupBroadcastSegmentFields(fieldDef, segments, emailBroadcastMode) {
  const filterSegments = segments.filter(s => s.type === 'FILTER')
  const pinpointSegments = segments.filter(s => s.type === 'USERGEN')
  const segmentIdFieldIndex = fieldDef.steps[0].fields.findIndex(f => f.key === 'segmentId')
  if (segmentIdFieldIndex === -1) { return }

  const baseSegmentField = fieldDef.steps[0].fields[segmentIdFieldIndex]
  fieldDef.steps[0].fields.splice(segmentIdFieldIndex, 1)

  const whatsappField = JSON.parse(JSON.stringify(baseSegmentField))
  whatsappField.condition = { key: 'channel', value: 'WHATSAPP', values: ['WHATSAPP'] }
  whatsappField.options = mapSegmentOptions(filterSegments)

  if (emailBroadcastMode === 'EXPERIMENTAL') {
    const broadcastModeField = {
      key: 'broadcastMode',
      type: 'radio',
      label: 'Broadcast Mode',
      defaultValue: 'LEGACY',
      placement: 'left',
      condition: { key: 'channel', value: 'EMAIL', values: ['EMAIL'] },
      options: [
        { key: 'LEGACY', label: 'LEGACY' },
        { key: 'NEW', label: 'NEW' },
      ],
      // eslint-disable-next-line camelcase
      v_required: { value: false, err: '' },
    }

    const emailPinpointField = JSON.parse(JSON.stringify(baseSegmentField))
    emailPinpointField.condition = {
      and: [
        { key: 'channel', values: ['EMAIL'] },
        { key: 'broadcastMode', values: ['LEGACY'] },
      ],
    }
    emailPinpointField.options = mapSegmentOptions(pinpointSegments)
    emailPinpointField.placement = 'right'

    const emailFilterField = JSON.parse(JSON.stringify(baseSegmentField))
    emailFilterField.condition = {
      and: [
        { key: 'channel', values: ['EMAIL'] },
        { key: 'broadcastMode', values: ['NEW'] },
      ],
    }
    emailFilterField.options = mapSegmentOptions(filterSegments)
    emailFilterField.placement = 'right'

    fieldDef.steps[0].fields.splice(segmentIdFieldIndex, 0, broadcastModeField, emailPinpointField, emailFilterField, whatsappField)
  } else {
    const emailField = JSON.parse(JSON.stringify(baseSegmentField))
    emailField.condition = { key: 'channel', value: 'EMAIL', values: ['EMAIL'] }
    emailField.options = mapSegmentOptions(emailBroadcastMode === 'NEW' ? filterSegments : pinpointSegments)
    fieldDef.steps[0].fields.splice(segmentIdFieldIndex, 0, emailField, whatsappField)
  }
}

export function getEmailBroadcastMode(orgId) {
  return AppHelper.getSetting(orgId, 'EMAIL_BROADCAST_MODE') || 'LEGACY'
}

export function isSesBroadcast(campaign) {
  return campaign && campaign.channel === 'EMAIL' && campaign.internalBroadcast === 'YES'
}

export function isPinpointBroadcast(campaign) {
  return campaign && campaign.channel === 'EMAIL' && campaign.internalBroadcast !== 'YES'
}

export function canCloneEmailBroadcast(campaign, orgId) {
  if (!campaign || campaign.channel !== 'EMAIL' || campaign.iType !== 'BROADCAST') { return true }
  const mode = getEmailBroadcastMode(orgId)
  if (mode === 'EXPERIMENTAL') { return true }
  if (mode === 'LEGACY') { return isPinpointBroadcast(campaign) }
  if (mode === 'NEW') { return isSesBroadcast(campaign) }
  return true
}

export function canStartEmailBroadcast(campaign, orgId) {
  return canCloneEmailBroadcast(campaign, orgId)
}

export function getEmailChannelLabel(campaign, orgId) {
  if (!campaign || campaign.channel !== 'EMAIL') { return campaign.channel }
  const mode = getEmailBroadcastMode(orgId)
  if (mode === 'LEGACY') {
    return isSesBroadcast(campaign) ? 'EMAIL (NEW)' : 'EMAIL'
  }
  if (mode === 'NEW') {
    return isPinpointBroadcast(campaign) ? 'EMAIL (LEGACY)' : 'EMAIL'
  }
  if (mode === 'EXPERIMENTAL') {
    return isSesBroadcast(campaign) ? 'EMAIL (NEW)' : 'EMAIL (LEGACY)'
  }
  return 'EMAIL'
}

export class CampaignsBase extends BaseEntityPageSlixta {
  constructor(props, options) {
    super(props, options);
    this.ms = AppConfig.CDP_MS.ENDPOINT
    this.campaignType = options.campaignType
    this.pageTitle = options.pageTitle
    this.orgId = this.props.match.params.orgId || 'PRODUCT'
    this.staffUserId = Controller.get().getUserId()
    this.attribsGroupId = 'settingscrmcontactattrs' + this.orgId
    this.breadCrumbs = [
      { title: Helper.getString('home'), to: this.baseRoute() },
      { title: Helper.getString('campaigns') },
      { title: this.pageTitle },
    ]
    this.toFetch = [
      { key: 'campaigns', ms: this.ms, method: 'GET', paths: ['campaigns'], queryParams: { orgId: this.orgId, pb: 'fieldDefCampaignAd,fieldDefCampaignBroadcast,fieldDefCampaignTransactional,fieldDefCampaignNotification,fieldDefCampaignDrip,fieldDefCampaignSchedule', limit: 100, orgIdiType: `${this.orgId}-${this.campaignType}` } },
      { key: 'crminboxes', ms: this.ms, method: 'GET', paths: ['crminboxs'], queryParams: { orgId: this.orgId, orgIdUserId: `${this.orgId}-${this.staffUserId}`, includeShared: 'true' } },
      { key: 'crmcontactattrs', ms: this.ms, method: 'GET', paths: ['items', `${this.attribsGroupId}-all`], queryParams: { orgId: this.orgId, groupId: this.attribsGroupId } },
    ]
    if (this.campaignType === 'BROADCAST') this.toFetch.push({ key: 'csegments', ms: this.ms, method: 'GET', paths: ['csegments'], queryParams: { orgId: this.orgId, limit: 100 } })
    if (['BROADCAST', 'NOTIFICATION', 'TRANSACTIONAL'].includes(this.campaignType)) {
      this.toFetch.push({ key: 'emailpages', method: 'GET', paths: ['emailpages'], queryParams: { orgId: this.orgId } })
      this.toFetch.push({ key: 'emailpages-PRODUCT', method: 'GET', paths: ['emailpages'], queryParams: { orgId: 'PRODUCT' } })
    }
    this.apiData = {
      orgId: this.orgId
    }
    this.pageKey = options.pageKey || this.pageTitle
    this.tableHeadersMap = {
      Index: (current) => current.__index + 1,
      Info: (current) => {
        let template = ''
        switch(current.channel) {
        case 'EMAIL':
          {
            const emailTemplate = this.emailPages.find(p => p.id === current.templateId)
            template = current.iType !== 'BROADCAST' && emailTemplate ? emailTemplate.title : '';
          }
          break;
        }
        return <div>
          <span className={css(Styles.line1)}>{current.title}</span>
          <br />
          {current.iType === 'DRIP' && current.steps && (<>
           <span className={css(Styles.line2)}>{`Steps: ${Object.keys(current.steps).length} | Days: ${findTotalDelayInDays(current.steps)}`}</span>
          <br /></>)}
          {current.sendingWindowDays && UiHelper.renderDaysInWeek(current.sendingWindowDays)}
          { current.sendingWindowStartTime && current.sendingWindowEndTime && (<>
          <span className={css(Styles.line2)}>{`${Helper.formatTimeFrom24to12Hour(current.sendingWindowStartTime)} to ${Helper.formatTimeFrom24to12Hour(current.sendingWindowEndTime)}`}</span>
          <br /></>)}
          {template && <><span className={css(Styles.line2)}>Template: {template}</span><br/></>}
          {current.iType !== 'DRIP' && <span className={css(Styles.line3)}>Created: {Helper.formatTimestamp(current.createdTs)}</span>}
          {this.renderExecutedOrScheduledTime(current)}
        </div>
      },
      Channel: (current) => {
        const label = this.campaignType === 'BROADCAST' && current.channel === 'EMAIL'
          ? getEmailChannelLabel(current, this.orgId)
          : current.channel
        return <div>
          <span className={css(Styles.line1)}>{label}</span>
        </div>
      },
    }
    switch(this.campaignType) {
    case 'BROADCAST':
      this.tableHeadersMap.Segment = (current) => {
        const segment = this.segments.find(s => s.id === current.segmentId)
        return segment ? segment.title : '';
      }
      break;
    case 'TRANSACTIONAL':
    case 'NOTIFICATION':
      this.tableHeadersMap.Event = (current) => current.transactionalEvent || current.notificationEvent
      break;
    case 'AD':
      delete this.tableHeadersMap.Channel
      this.tableHeadersMap.Media = (current) => current.adMediaType
      this.tableHeadersMap['Budget (USD)'] = (current) => current.adTotalBudget
      this.tableHeadersMap.Targeting = (current) => `${current.adLocation}, ${current.adRadius} miles`
      this.tableHeadersMap.Schedule = (current) => {
        return <>
          {current.scheduleTs && <p className={css(Styles.line2)}>{Helper.formatTimestamp(current.scheduleTs)}</p>}
          {current.scheduleEndTs && <p className={css(Styles.line2)}>{Helper.formatTimestamp(current.scheduleEndTs)}</p>}
        </>
      }
      break;
    case 'DRIP':
      delete this.tableHeadersMap.Channel
      break;
    }
    this.tableHeadersMap.Status = (current) => current.state
    this.tableHeaders = Object.keys(this.tableHeadersMap)
    this.cellWidths = {
      Index: Styles.cellWidthSmall,
      Info: Styles.cellWidthWide,
      Channel: Styles.cellWidthChannel,
    }
    this.headerActions = [
      {
        label: 'Add new',
        onClick: () => this.openModalForAdd(),
      },
      {
        label: 'Refresh',
        tooltip: 'Refresh',
        icon: 'refresh',
        type: 'icon-label',
        onClick: () => this.fetchItems(),
      }
    ]
    this.uploadMs = AppConfig.CDP_MS.ENDPOINT
  }

  renderExecutedOrScheduledTime(current) {
    let executedTs = null;
    let scheduledTs = null;

    if (current.state === 'COMPLETED') {
      if (current.executeTs) {
        executedTs = current.executeTs;
      } else if (current.scheduleType === 'LATER' && current.scheduleTs) {
        executedTs = current.scheduleTs;
      } else if (current.scheduleType === 'IMMEDIATE' && current._extCampaign && current._extCampaign.creationDate) {
        executedTs = current._extCampaign.creationDate;
      }
    } else if (current.state === 'SCHEDULED') {
      if (current.scheduleType === 'LATER' && current.scheduleTs) {
        scheduledTs = current.scheduleTs;
      } else if (current.scheduleType === 'IMMEDIATE' && current._extCampaign && current._extCampaign.creationDate) {
        scheduledTs = current._extCampaign.creationDate;
      }
    }

    return (
      <>
        {scheduledTs && (
          <>
            <br />
            <span className={css(Styles.line3)}>
              Scheduled: {Helper.formatTimestamp(scheduledTs)}
            </span>
          </>
        )}

        {executedTs && (
          <>
            <br />
            <span className={css(Styles.line3)}>
              Executed: {Helper.formatTimestamp(executedTs)}
            </span>
          </>
        )}
      </>
    );
  }

  onFetchSuccess(results, options) {
    const { fetchNext } = options || {}
    if (fetchNext) {
      this.campaigns = results[0]
    } else {
      this.campaigns = this.getResult(results, 'campaigns')
      this.csegments = this.getResult(results, 'csegments')
      this.emailpagesOrg = this.getResult(results, 'emailpages')
      this.emailpagesPRODUCT = this.getResult(results, 'emailpages-PRODUCT')
      this.sharedCrmInboxes = CRMHelper.filterInboxes(this.getResult(results, 'crminboxes').items, 'trigger')
      this.crmContactAttrs = this.getResult(results, 'crmcontactattrs')
      const showTenantNameInHeader = AppHelper.isSettingEnabled(this.orgId, 'TENANT_NAME_IN_HEADER')
      if (showTenantNameInHeader) {
        this.pageTitle = UiHelper.getOrgTitle() || this.pageTitle
      }

      this.fieldDefCampaignAd = this.campaigns.fieldDefCampaignAd
      this.fieldDefCampaignBroadcast = this.campaigns.fieldDefCampaignBroadcast
      this.fieldDefCampaignTransactional = this.campaigns.fieldDefCampaignTransactional
      this.fieldDefCampaignNotification = this.campaigns.fieldDefCampaignNotification
      this.fieldDefCampaignDrip = this.campaigns.fieldDefCampaignDrip
      this.fieldDefCampaignSchedule = this.campaigns.fieldDefCampaignSchedule

      restrictChannels(this.fieldDefCampaignAd, ['OOH'], 'OOH')
      restrictChannels(this.fieldDefCampaignBroadcast, ['EMAIL'].concat(AppHelper.isWABroadcastsEnabled(this.orgId) ? ['WHATSAPP'] : []), 'EMAIL')
      restrictChannels(this.fieldDefCampaignTransactional, ['EMAIL'], 'EMAIL')
      restrictChannels(this.fieldDefCampaignNotification, ['EMAIL'], 'EMAIL')

      this.fieldDefCampaignBroadcast = UiHelper.removeFields(this.fieldDefCampaignBroadcast, ['templateId'])
      this.fieldDefCampaignBroadcast = UiHelper.reflowFields(this.fieldDefCampaignBroadcast)

      if (this.campaignType === 'AD') {
        this.fieldDefCampaignSchedule.disableAlwaysFormFields = ['scheduleType']
      } else {
        UiHelper.fieldDefDeleteField(this.fieldDefCampaignSchedule, 'scheduleEndTs')
      }

      if (this.csegments) {
        this.segments = this.csegments.items.filter(i => i.state === 'LIVE')
        this.filterSegments = this.segments.filter(s => s.type === 'FILTER')
        this.pinpointSegments = this.segments.filter(s => s.type === 'USERGEN')
        this.emailBroadcastMode = getEmailBroadcastMode(this.orgId)
        setupBroadcastSegmentFields(this.fieldDefCampaignBroadcast, this.segments, this.emailBroadcastMode)
      } else {
        this.segments = []
      }

      if (this.emailpagesOrg && this.emailpagesPRODUCT) {
        this.emailPages = [
          ...this.emailpagesOrg.items,
          ...this.emailpagesPRODUCT.items.filter(i => i.state === 'LIVE').map(i => ({...i, title: `[${i.title}]`})),
        ]
        UiHelper.populateOptions(this.fieldDefCampaignBroadcast, {items: this.emailPages}, 'templateId')
        UiHelper.populateOptions(this.fieldDefCampaignTransactional, {items: this.emailPages}, 'templateId')
        UiHelper.populateOptions(this.fieldDefCampaignNotification, {items: this.emailPages}, 'templateId')
      } else {
        this.emailPages = []
      }

      const dripTriggerInboxField = this.fieldDefCampaignDrip
        && this.fieldDefCampaignDrip.steps
        && this.fieldDefCampaignDrip.steps[0]
        && this.fieldDefCampaignDrip.steps[0].fields.find(f => f.key === 'triggerInboxId')
      if (dripTriggerInboxField) {
        dripTriggerInboxField.options = CRMHelper.toInboxSelectOptions(this.sharedCrmInboxes)
      }
      const dripTagOptions = {items: Helper.extractAttrs(this.crmContactAttrs, 'tags').map(t => ({id: t, title: t}))}
      UiHelper.populateOptions(this.fieldDefCampaignDrip, dripTagOptions, 'triggerOnTags')
      UiHelper.populateOptions(this.fieldDefCampaignDrip, dripTagOptions, 'stopOnTags')
    }

    if (this.campaignType === 'BROADCAST') {
      this.handleNextpage(this.campaigns)
    }

    this.campaigns.items.forEach(c => {
      CdpHelper.patchDripCampaignTracking(c)
    })
    const visibleItems = this.campaignType === 'DRIP'
      ? CdpHelper.filterOutDeletedCampaigns(this.campaigns.items)
      : this.campaigns.items
    const sortedItems = visibleItems.sort((a, b) => b.createdTs - a.createdTs)
    this.setState({
      items: fetchNext ? this.state.items.concat(sortedItems) : sortedItems
    })
  }

  openModalForAdd() {
    super.openModalForAdd({steps: [{ fields: getCampaignFields(this) }]});
  }
  onAdd() {
    this.apiData = {
      orgId: this.orgId,
      iType: this.campaignType,
    }
    this.setState({
      apiMethod: 'POST',
      apiEndPoint: ApiHelper.makeUrlPath2({ ms: this.ms, paths: ['campaigns'], queryParams: {orgId: this.orgId} }),
    });
  }

  openModalForEdit(current) {
    const fields = getCampaignFields(this)
    fields.find(f => f.key === 'title')
    const disableAlwaysFormFields = current.state === 'DRAFT' ? [] : ['channel', 'templateId', 'transactionalEvent', 'notificationEvent', 'segmentId']
    this.apiData = {
      orgId: this.orgId,
    }
    super.openModalForEdit(current, {steps: [{ fields }], disableAlwaysFormFields});
  }

  onEdit(current) {
    this.setState({
      apiMethod: 'PUT',
      apiEndPoint: ApiHelper.makeUrlPath2({ ms: this.ms, paths: ['campaigns', current.id], queryParams: {orgId: this.orgId} }),
    });
  }

  async updateCampaignState(current, action) {
    if (['AD', 'BROADCAST'].includes(current.iType) && action === 'start') {
      this.apiData = {
        updateAction: action,
        orgId: this.orgId
      }
      super.openModalForEdit(current, this.fieldDefCampaignSchedule)
    } else {
      this.apiData = {
        updateAction: action,
        orgId: this.orgId
      }
      const fields = [{
        key: 'updateAction',
        label: 'Update action',
        type: 'edit_text',
        hint: 'Update action',
        // eslint-disable-next-line camelcase
        v_required: { value: true, err: 'Provide update action' },
        placement: 'left',
        value: action,
        disabled: true,
      }]
      current.updateAction = action
      super.openModalForEdit(current, {steps: [{ fields }]});
    }
  }

  onActions = (current) => {
    const actions = []
    if (current.iType === 'BROADCAST') {
      actions.push(
        <Link to={this.baseRoute() + '/campaigns/' + current.id} key={current.id}>
          <Tooltip title='View Campaign' placement='top-start'>
            <span className={css(AppStyles.entityTableLinkIcon)}>
              <i className='material-icons-outlined'>arrow_forward_ios</i>
            </span>
          </Tooltip>
        </Link>
      )
    } else {
      if (current.iType === 'DRIP') {
        actions.push(
          <Link to={this.baseRoute() + '/campaigns/sequences/' + current.id} key={current.id}>
            <Tooltip title='View Sequence' placement='top-start'>
              <span className={css(AppStyles.entityTableLinkIcon)}>
                <i className='material-icons-outlined'>arrow_forward_ios</i>
              </span>
            </Tooltip>
          </Link>
        )
      } else {
        actions.push(UiHelper.buttonEdit(this, current))
        if(['DRAFT', 'PAUSED'].includes(current.state)) {
          actions.push(
            <Tooltip title='Start' placement='top-start' key={current.id + '-start'}>
              <span className={css(AppStyles.entityTableLinkIcon)}>
                <i className='material-icons-outlined' onClick={() => {
                  if(current.state === 'DRAFT') {
                    this.updateCampaignState(current, 'start')
                  } else if (current.state === 'PAUSED') {
                    this.updateCampaignState(current, 'resume')
                  }
                }}>
                send
                </i>
              </span>
            </Tooltip>
          )
        }
        if(['ACTIVE', 'LIVE', 'SCHEDULED', 'REVIEW'].includes(current.state)) {
          actions.push(
            <Tooltip title='Pause' placement='top-start' key={current.id + '-pause'}>
              <span className={css(AppStyles.entityTableLinkIcon)}>
                <i className='material-icons-outlined' onClick={() => {
                  this.updateCampaignState(current, 'pause')
                }}>
                pause
                </i>
              </span>
            </Tooltip>
          )
        }
        if(['COMPLETED', 'PAUSED', 'LIVE', 'ACTIVE'].includes(current.state) && ['AD', 'BROADCAST'].includes(current.iType)) {
          actions.push(
            <Link to={this.baseRoute() + '/campaigns/' + current.id + '/results'} key={current.id + '-results'}>
              <Tooltip title='Results' placement='top-start'>
                <span className={css(AppStyles.entityTableLinkIcon)}>
                  <i className='material-icons-outlined'>trending_up</i>
                </span>
              </Tooltip>
            </Link>
          )
        }
      }
    }
    return actions
  }

  onMenuItems = (current) => {
    const actions = []
    const canClone = canCloneEmailBroadcast(current, this.orgId)
    const menuItems = [
      {
        current: current,
        key: current.id + 'create-copy',
        action: canClone ? () => {
          UiHelper.openModalForCreateCopy.call(this, current, 'CAMPAIGN')
        } : undefined,
        disabled: !canClone,
        tooltip: !canClone ? 'Copy not available for this broadcast mode' : '',
        title: Helper.getString('createcopy'),
        icon:'copy',
      },
    ]
    menuItems.map(item => {
      actions.push(UiHelper.actionButton(item))
    })
    return actions
  }

  onTableValue(current, index) {
    if (!this.tableHeaders[index]) { return '' }
    return this.tableHeadersMap[this.tableHeaders[index]](current)
  }

  updateLoaderUi(isLoading) {
    this.setState({
      fetchState: isLoading ? ApiHelper.State.LOADING : ApiHelper.State.READY
    });
  }
}

const Styles = StyleSheet.create({
  line1: {
    fontSize: 16
  },
  line2: {
    fontSize: 12,
    color: 'gray'
  },
  line3: {
    fontSize: 12,
    color: '#AAAAAA'
  },
  cellWidthSmall: {
    width: '8%',
    minWidth: 56,
  },
  cellWidthNormal: {
    width: '18%',
    minWidth: 120,
  },
  cellWidthWide: {
    width: '38%',
    minWidth: 200,
  },
  cellWidthChannel: {
    width: '16%',
    minWidth: 160,
    whiteSpace: 'nowrap',
  },
})